import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { MdOutlineFileDownload } from "react-icons/md";

import api from "../../../services/api";

const Invoice = ({ project }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const query = { projectId: project._id, limit: 100 };
        const { data, ok } = await api.post(`/invoice/search`, query);
        if (!ok) return toast.error("Error while fetching data");
        setData(data);
        setTotal(data.length);
      } catch (error) {
        toast.error("Error while refreshing");
        console.error(error);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const invoicesAmount = data.reduce((acc, e) => acc + e.total, 0);
  const dueInvoices = data.filter((e) => e.sent === "yes" && e.received === "no");
  const invoicesNotPaid = dueInvoices.length || 0;
  const invoicesNotPaidAmount = dueInvoices.reduce((acc, e) => acc + e.totalRemaining, 0);

  return (
    <div className="p-4 flex gap-4">
      <div className="flex-1 min-w-0">
        {loading ? (
          <div>loading...</div>
        ) : (
          <div className="w-full p-6 bg-white border border-gray-300 rounded">
            <table className="table-fixed w-full">
              <thead className="border-b border-gray-300">
                <tr>
                  <th colSpan={2} className="text-left font-semibold text-sm p-2">
                    Name
                  </th>
                  <th className="text-left font-semibold text-sm p-2">Date</th>
                  <th className="text-left font-semibold text-sm p-2">Budget</th>
                  <th className="text-left font-semibold text-sm p-2">Reference</th>
                  <th className="text-left font-semibold text-sm p-2">Sent</th>
                  <th className="text-left font-semibold text-sm p-2">Received</th>
                  <th className="text-right font-semibold text-sm p-2 pr-16">Total</th>
                </tr>
              </thead>
              <tbody className="">
                {data.length > 0 ? (
                  data.map((item) => <ItemRow key={item._id} item={item} />)
                ) : (
                  <tr>
                    <td colSpan={8} className="p-2 text-sm text-center text-gray-400">
                      No invoice found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="w-64 flex-shrink-0">
        <div className="bg-white border border-gray-200 rounded-md p-4 space-y-4 sticky top-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-600">Invoices</h2>
            <p className="text-xl font-bold">{total}</p>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-600">Total amount</h2>
            <p className="text-xl font-bold">{(invoicesAmount || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</p>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-600">Invoices dues</h2>
            <p className="text-xl font-bold text-red-600">{invoicesNotPaid}</p>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-600">Amount due</h2>
            <p className="text-xl font-bold text-red-600">{(invoicesNotPaidAmount || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</p>
            {dueInvoices.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-gray-400">Sent &amp; not received</p>
                {dueInvoices.map((inv) => (
                  <div key={inv._id} className="flex justify-between gap-2 text-xs text-gray-700">
                    <span className="truncate">{inv.name || inv.index}</span>
                    <span className="flex-shrink-0 font-medium">
                      {(inv.totalRemaining || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between gap-2 text-xs font-semibold text-gray-900 pt-1 border-t border-gray-100">
                  <span>Total</span>
                  <span>{(invoicesNotPaidAmount || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

const ItemRow = ({ item }) => {
  const onDownload = (e) => {
    e.stopPropagation();
    e.preventDefault();
    let alink = document.createElement("a");
    alink.href = item.file;
    alink.setAttribute("target", "_blank");
    alink.click();
  };

  return (
    <tr className="hover:bg-gray-100 h-12" onClick={() => window.open(`/invoice/${item._id}`, "_blank")}>
      <td colSpan={2} className="p-2 text-sm">
        {item.name}
      </td>
      <td className="text-left p-2 text-sm">{new Date(item.date).toLocaleDateString("en-US", { year: "numeric", month: "long" })}</td>
      <td className="text-left p-2 text-sm">{item.budgetName || <span className="text-gray-400">—</span>}</td>
      <td className="text-left p-2 text-sm">{item.index}</td>
      <td className="text-left p-2 text-sm">
        <span className={`px-2 py-1 rounded ${item.sent === "yes" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
          {item.sent} {item.sent === "yes" && `(${new Date(item.sentAt).toLocaleString().substring(0, 10)})`}
        </span>
      </td>
      <td className="text-left p-2 text-sm">
        <span className={`px-2 py-1 rounded ${item.received === "yes" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
          {renderReceivedStatus(item)}
        </span>
      </td>
      <td className="text-left text-sm">
        <div className="w-full flex items-center gap-4 p-2">
          <div className="flex-1 text-right font-medium">{(item.total || 0).toLocaleString("fr", { style: "currency", currency: "EUR" })}</div>
          {item.file ? (
            <button onClick={onDownload} className="text-sm h-8 w-8 border border-gray-200 hover:bg-gray-100 rounded-md flex items-center justify-center">
              <MdOutlineFileDownload className="text-gray-600" />
            </button>
          ) : (
            <div className="w-8" />
          )}
        </div>
      </td>
    </tr>
  );
};

const renderReceivedStatus = ({ received, receivedAt, dueAt }) => {
  const formatDays = (days) => (days === 1 ? "1 day" : `${days} days`);

  const toDate = (d) => (d && !isNaN(new Date(d)) ? new Date(d) : null);
  const dDue = toDate(dueAt),
    dRec = toDate(receivedAt),
    now = new Date(),
    oneDay = 1000 * 60 * 60 * 24;

  if (!dDue) return received === "yes" && dRec ? `yes (${dRec.toLocaleDateString("fr-FR")})` : "no";

  if (received === "no") return now < dDue ? `no (${formatDays(Math.ceil((dDue - now) / oneDay))} left)` : `no (${formatDays(Math.ceil((now - dDue) / oneDay))} late)`;

  if (received === "yes" && dRec)
    return dRec > dDue ? `yes (${dRec.toLocaleDateString("fr-FR")}, ${formatDays(Math.ceil((dRec - dDue) / oneDay))} late)` : `yes (${dRec.toLocaleDateString("fr-FR")})`;

  return "no";
};

export default Invoice;
